import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { sqScorecardStore, sqTrendScalingColumnStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { getMaterializedTable } from '@/tableDefinitionEditor/materializedTable.utilities';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import { getItemsInScalingTableRow } from '@/tableDefinitionEditor/scalingTable.utilities';
import { CSV_TYPES, ID_COLUMN_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { isItemTableCell } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { findItem } from '@/trendData/itemStore.utilities';
import { fetchAllItems, setTrendItemProps } from '@/trendData/trend.actions';
import { swapItemsInTrendStores } from '@/trendData/trendItemStores.actions';
import { DUMMY_ITEM_PREFIX } from '@/utilities/formula.constants';
import { randomUUID } from '@/utilities/guid.utilities';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { FilterTypeEnum } from '@seeqdev/api/model/ValueFilter';
import type { PushOption } from '@/core/flux.service';
import type { ScalingTableSwapRow } from '@/search/search.types';
import type { ItemTableCell, ProcessedTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ScalingColumnItem } from '@/trendData/trendData.types';

export async function addScalingColumn(
  column: ScalingColumnItem,
  props?: {
    sampleItemName?: string;
  },
  option?: PushOption,
) {
  if (!findItem(sqTrendScalingColumnStore.getState().items, column.id)) {
    const payload = {
      id: column.id,
      name: `Column: ${column.name} -- Sample Item: ${props?.sampleItemName}`,
      props: props ?? {},
    };
    sqTrendScalingColumnStore.actions.addScalingColumn(payload);

    setTrendItemProps(column.id, props, option);
  }
  return [];
}

export async function swapScalingTableRows(
  swapRow: { id: string; name: string },
  swapColumns: ScalingTableSwapRow[],
  allItemsCount: number,
): Promise<void> {
  const { data: scalingTableData } = await sqTableDefinitionsApi.getTableDefinition({ id: swapColumns[0].tableId });
  const isScalingTableCSV = CSV_TYPES.includes(scalingTableData.tableType);
  const itemIdColumnName = isScalingTableCSV
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  if (swapColumns.some((column) => ID_COLUMN_TYPES.includes(column.columnName))) {
    swapColumns
      .filter(({ columnName }) => ID_COLUMN_TYPES.includes(columnName))
      .forEach((column) => (column.columnName = itemIdColumnName));
  }

  const tableData = await getMaterializedTable(
    swapColumns[0].tableId,
    swapColumns.map((column) => column.columnName),
    swapColumns.map((column) => ({
      uuidColumn: column.columnName,
      propertyNames: [
        SeeqNames.Properties.Name,
        SeeqNames.Properties.Type,
        SeeqNames.Properties.CreatedByModelInstance,
      ],
    })),
    undefined,
    undefined,
    undefined,
    {
      valueFilter: {
        filterType: FilterTypeEnum.EQUALS,
        columnName: itemIdColumnName,
        value: { value: swapRow.id },
      },
    },
  );

  const processedMaterializedTable = processMaterializedTable(
    tableData,
    scalingTableData.columnDefinitions.map((column) => {
      return {
        ...column,
        displayName: column.columnName,
        additionalProperties: [SeeqNames.Properties.CreatedByModelInstance],
      };
    }),
  );

  if (!processedMaterializedTable || !processedMaterializedTable.rows.length) {
    warnToast({
      messageKey: 'SCALING_ROW_REPLACE.NOT_POSSIBLE',
      messageParams: {
        SWAP_ROW_NAME: swapRow.name,
      },
    });
    return;
  }

  const materializedTableRows = processedMaterializedTable?.rows[0]
    .map((row, index) => ({
      ...row,
      columnName: processedMaterializedTable.headers[index].name,
    }))
    .filter((cell: ProcessedTableCell) => isItemTableCell(cell));

  const swaps = _.zipObject(
    swapColumns.map((col) => col.id),
    swapColumns.map((col) => {
      const matchedRow = materializedTableRows.find((row) => row.columnName === col.columnName);
      return matchedRow ? (matchedRow as ItemTableCell).uuid : `${DUMMY_ITEM_PREFIX}-${randomUUID()}`;
    }),
  );

  swapItemsInTrendStores({ swaps });
  sqScorecardStore.actions.swapItems(swaps);
  // For each swap item, update the props to reflect the new row reference
  _.forEach(swapColumns, (scalingRowItem) => {
    const { id, ...scalingRowItemPropsWithoutId } = scalingRowItem;
    const matchedRow = materializedTableRows.find((row) => row.columnName === scalingRowItem.columnName);
    const instanceId =
      matchedRow && isItemTableCell(matchedRow)
        ? matchedRow.fetchedProperties?.[SeeqNames.Properties.CreatedByModelInstance]
        : undefined;

    setTrendItemProps(swaps[scalingRowItem.id], {
      ...scalingRowItemPropsWithoutId,
      columnName: scalingRowItem.columnName,
      columnId: scalingTableData.columnDefinitions.find((column) => column.columnName === scalingRowItem.columnName)
        ?.id,
      instanceId,
      rowName: swapRow.name,
      rowId: swapRow.id,
      stringEnum: undefined,
      isStringSeries: undefined,
    });
  });
  successToast({
    messageKey: 'SWAPS.SUCCESS_V2',
    messageParams: {
      successfulSwapsCount: _.size(swaps),
      allItemsCount,
    },
  });

  fetchAllItems();
}

export function setScalingColumnsUpdated(scalingColumnsUpdated: boolean) {
  flux.dispatch('TREND_SET_SCALING_COLUMNS_UPDATED', scalingColumnsUpdated);
}

export async function getScalingTableItemProps(tableId: string, rowId: string, itemId: string, columnId?: string) {
  const { materializedTableRow, scalingTableData } = await getItemsInScalingTableRow(tableId, rowId);
  const isPlaceholder = itemId.startsWith(DUMMY_ITEM_PREFIX);

  const columnFromTable = scalingTableData.columnDefinitions.find((column) => column.id === columnId);
  const columnFromMaterializedTable = materializedTableRow.find((cell) => cell.uuid === itemId);
  const column = columnId ? columnFromTable?.columnName : columnFromMaterializedTable?.columnName;

  if (!column) {
    return;
  }

  const itemAtCoordinates = materializedTableRow.find((cell) => cell.columnName === column);
  const correctItemAtCoordinates = itemAtCoordinates && itemAtCoordinates.uuid === itemId;

  const navigationColumn = scalingTableData.columnDefinitions.find(
    (column) => column.id === scalingTableData.navigationColumnId,
  );

  const navigationRowName = navigationColumn
    ? materializedTableRow.find((column) => column.columnName === navigationColumn.columnName)?.fetchedProperties?.[
        navigationColumn.displayProperty || SeeqNames.Properties.Name
      ]
    : undefined;

  const isCorrectItemOrPlaceholder = correctItemAtCoordinates || isPlaceholder;
  const rowName = isCorrectItemOrPlaceholder
    ? navigationRowName
      ? navigationRowName
      : materializedTableRow.find((column) => column.uuid === rowId)?.fetchedProperties?.[SeeqNames.Properties.Name]
    : undefined;
  const tableName = isCorrectItemOrPlaceholder ? scalingTableData.name : undefined;
  const columnName = isCorrectItemOrPlaceholder ? column : undefined;
  const columnIdProp =
    columnId ??
    scalingTableData.columnDefinitions.find((column) => column.columnName === columnFromMaterializedTable?.columnName)
      ?.id;

  if (isCorrectItemOrPlaceholder) {
    setTrendItemProps(itemId, {
      tableId,
      tableName,
      rowName,
      rowId,
      columnName,
      columnId: columnIdProp,
      previousTableId: undefined,
      previousRowId: undefined,
      previousColumnId: undefined,
    });
    return;
  }

  setTrendItemProps(itemId, {
    previousTableId: tableId,
    previousRowId: rowId,
    previousColumnId: columnIdProp,
    tableId: undefined,
    tableName: undefined,
    rowName: undefined,
    rowId: undefined,
    columnName: undefined,
    columnId: undefined,
  });
}
